/**
 * Telephony Queue Manager
 * =======================
 * Manage phone queues with user assignments by profile
 * Similar to WhatsApp Queue Manager
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE as TELEPHONY_API } from './config';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;

const TelephonyQueueManager = ({ currentUser }) => {
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    extension_number: '',
    profiles: [],
    members: [],
    strategy: 'round_robin',
    max_wait_time: 300,
    max_callers: 10,
    wrap_up_time: 10,
    position_announcement: true,
    welcome_message: '',
    hold_music_url: '',
    is_active: true,
    workingHours: {
      enabled: false,
      start: '08:30',
      end: '17:00',
      days: [0, 1, 2, 3, 4]
    }
  });

  // Available profiles
  const PROFILES = [
    { id: 'Admin', label: 'מנהל מערכת' },
    { id: 'Standard User', label: 'משתמש רגיל' },
    { id: 'service_center', label: 'מוקד שירות' },
    { id: 'מוקד שירות', label: 'מוקד שירות' },
    { id: 'sales_center', label: 'מוקד מכירות' },
    { id: 'מוקד מכירה', label: 'מוקד מכירה' },
    { id: 'technician', label: 'טכנאי' },
    { id: 'טכנאים', label: 'טכנאים' },
    { id: 'call_center_manager', label: 'מנהל מוקד' },
    { id: 'office_management', label: 'הנהלה משרדית' },
    { id: 'הנהלה', label: 'הנהלה' },
    { id: 'הנהלה מוקד שירות', label: 'הנהלה מוקד שירות' },
    { id: 'accounting', label: 'הנהלת חשבונות' },
    { id: 'מעבדה', label: 'מעבדה' }
  ];

  const STRATEGIES = [
    { id: 'round_robin', label: 'סבב (Round Robin)', desc: 'חלוקה שווה בין הנציגים' },
    { id: 'least_calls', label: 'הפחות עמוס', desc: 'הפנייה לנציג עם הכי פחות שיחות' },
    { id: 'longest_idle', label: 'הכי זמן פנוי', desc: 'הפנייה לנציג שפנוי הכי הרבה זמן' },
    { id: 'ring_all', label: 'צלצל לכולם', desc: 'צלצול לכל הנציגים בו זמנית' },
    { id: 'random', label: 'אקראי', desc: 'בחירה אקראית של נציג' }
  ];

  const DAYS = [
    { id: 0, label: "א'" },
    { id: 1, label: "ב'" },
    { id: 2, label: "ג'" },
    { id: 3, label: "ד'" },
    { id: 4, label: "ה'" },
    { id: 5, label: "ו'" },
    { id: 6, label: "ש'" }
  ];

  // Load queues
  const loadQueues = useCallback(async () => {
    try {
      const response = await fetch(`${TELEPHONY_API}/queues`);
      const data = await response.json();
      setQueues(data.data || []);
    } catch (err) {
      console.error('Error loading queues:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users
  const loadUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/users/list`);
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, []);

  useEffect(() => {
    loadQueues();
    loadUsers();
  }, [loadQueues, loadUsers]);

  const handleSave = async () => {
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const endpoint = editingQueue
        ? `${TELEPHONY_API}/queues/${editingQueue.id}`
        : `${TELEPHONY_API}/queues`;

      const response = await fetch(endpoint, {
        method: editingQueue ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        setShowEditor(false);
        setEditingQueue(null);
        resetForm();
        loadQueues();
      }
    } catch (err) {
      console.error('Error saving queue:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם למחוק את התור?')) return;

    try {
      await fetch(`${TELEPHONY_API}/queues/${id}`, { method: 'DELETE' });
      loadQueues();
    } catch (err) {
      console.error('Error deleting queue:', err);
    }
  };

  const handleToggle = async (id) => {
    try {
      await fetch(`${TELEPHONY_API}/queues/${id}/toggle`, { method: 'POST' });
      loadQueues();
    } catch (err) {
      console.error('Error toggling queue:', err);
    }
  };

  const handleEdit = (queue) => {
    setEditingQueue(queue);
    setForm({
      name: queue.name || '',
      description: queue.description || '',
      extension_number: queue.extension_number || '',
      profiles: queue.profiles || [],
      members: queue.members || [],
      strategy: queue.strategy || 'round_robin',
      max_wait_time: queue.max_wait_time || 300,
      max_callers: queue.max_callers || 10,
      wrap_up_time: queue.wrap_up_time || 10,
      position_announcement: queue.position_announcement !== false,
      welcome_message: queue.welcome_message || '',
      hold_music_url: queue.hold_music_url || '',
      is_active: queue.is_active !== false,
      workingHours: queue.workingHours || {
        enabled: false,
        start: '08:30',
        end: '17:00',
        days: [0, 1, 2, 3, 4]
      }
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      extension_number: '',
      profiles: [],
      members: [],
      strategy: 'round_robin',
      max_wait_time: 300,
      max_callers: 10,
      wrap_up_time: 10,
      position_announcement: true,
      welcome_message: '',
      hold_music_url: '',
      is_active: true,
      workingHours: {
        enabled: false,
        start: '08:30',
        end: '17:00',
        days: [0, 1, 2, 3, 4]
      }
    });
  };

  const toggleProfile = (profileId) => {
    setForm(prev => ({
      ...prev,
      profiles: prev.profiles.includes(profileId)
        ? prev.profiles.filter(p => p !== profileId)
        : [...prev.profiles, profileId]
    }));
  };

  const toggleMember = (email) => {
    setForm(prev => ({
      ...prev,
      members: prev.members.includes(email)
        ? prev.members.filter(m => m !== email)
        : [...prev.members, email]
    }));
  };

  const toggleDay = (dayId) => {
    setForm(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        days: prev.workingHours.days.includes(dayId)
          ? prev.workingHours.days.filter(d => d !== dayId)
          : [...prev.workingHours.days, dayId].sort()
      }
    }));
  };

  // Get users filtered by selected profiles
  const filteredUsers = users.filter(user =>
    form.profiles.length === 0 || form.profiles.includes(user.profile)
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>ניהול תורי טלפוניה</h2>
          <p style={styles.subtitle}>הגדר תורים להפניית שיחות נכנסות לנציגים</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingQueue(null);
            setShowEditor(true);
          }}
          style={styles.addButton}
        >
          + תור חדש
        </button>
      </div>

      {/* Queues List */}
      {loading ? (
        <div style={styles.loading}>טוען...</div>
      ) : queues.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📞</span>
          <p>אין תורי טלפוניה מוגדרים</p>
          <button onClick={() => setShowEditor(true)} style={styles.emptyButton}>
            צור תור ראשון
          </button>
        </div>
      ) : (
        <div style={styles.queuesList}>
          {queues.map(queue => (
            <div key={queue.id} style={styles.queueCard}>
              <div style={styles.queueHeader}>
                <div style={styles.queueInfo}>
                  <div style={styles.queueName}>
                    <span style={styles.queueIcon}>🛎️</span>
                    {queue.name}
                    {queue.extension_number && (
                      <span style={styles.extensionBadge}>שלוחה {queue.extension_number}</span>
                    )}
                    {!queue.is_active && <span style={styles.inactiveBadge}>לא פעיל</span>}
                  </div>
                  {queue.description && (
                    <div style={styles.queueDescription}>{queue.description}</div>
                  )}
                </div>
                <div style={styles.queueActions}>
                  <label style={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={queue.is_active}
                      onChange={() => handleToggle(queue.id)}
                      style={styles.toggleInput}
                    />
                    <span style={{
                      ...styles.toggleSwitch,
                      backgroundColor: queue.is_active ? '#0176d3' : '#ccc'
                    }}>
                      <span style={{
                        ...styles.toggleKnob,
                        transform: queue.is_active ? 'translateX(20px)' : 'translateX(0)'
                      }} />
                    </span>
                  </label>
                  <button onClick={() => handleEdit(queue)} style={styles.iconButton}>
                    ✏️
                  </button>
                  <button onClick={() => handleDelete(queue.id)} style={styles.iconButton}>
                    🗑️
                  </button>
                </div>
              </div>

              <div style={styles.queueDetails}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>אסטרטגיה:</span>
                  <span style={styles.detailValue}>
                    {STRATEGIES.find(s => s.id === queue.strategy)?.label || queue.strategy}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>פרופילים:</span>
                  <span style={styles.detailValue}>
                    {queue.profiles?.length > 0
                      ? queue.profiles.map(p => PROFILES.find(pr => pr.id === p)?.label || p).join(', ')
                      : 'כל הפרופילים'}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>נציגים:</span>
                  <span style={styles.detailValue}>
                    {queue.member_count || queue.members?.length || 0} משתמשים
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>ממתינים:</span>
                  <span style={styles.detailValue}>
                    {queue.waiting_count || 0} שיחות
                  </span>
                </div>
              </div>

              {(queue.members?.length > 0 || queue.member_count > 0) && (
                <div style={styles.membersList}>
                  {(queue.members || []).slice(0, 5).map((member, idx) => {
                    const user = users.find(u => u.email === (member.email || member));
                    const name = member.display_name || user?.firstName || user?.name || (member.email || member).split('@')[0];
                    return (
                      <span key={idx} style={styles.memberChip}>
                        {name}
                      </span>
                    );
                  })}
                  {(queue.members?.length || 0) > 5 && (
                    <span style={styles.memberMore}>+{queue.members.length - 5}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{editingQueue ? 'עריכת תור' : 'תור חדש'}</h3>
              <button onClick={() => setShowEditor(false)} style={styles.closeButton}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {/* Basic Info */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>פרטי התור</h4>
                <div style={styles.formGroup}>
                  <label style={styles.label}>שם התור *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    style={styles.input}
                    placeholder="לדוגמה: תור שירות לקוחות"
                  />
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>מספר שלוחה</label>
                    <input
                      type="text"
                      value={form.extension_number}
                      onChange={(e) => setForm(prev => ({ ...prev, extension_number: e.target.value }))}
                      style={styles.input}
                      placeholder="לדוגמה: 200"
                      dir="ltr"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>אסטרטגיית חלוקה</label>
                    <select
                      value={form.strategy}
                      onChange={(e) => setForm(prev => ({ ...prev, strategy: e.target.value }))}
                      style={styles.select}
                    >
                      {STRATEGIES.map(s => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>תיאור</label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    style={styles.input}
                    placeholder="תיאור קצר של התור"
                  />
                </div>
              </div>

              {/* Queue Settings */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>הגדרות תור</h4>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>זמן המתנה מקסימלי (שניות)</label>
                    <input
                      type="number"
                      value={form.max_wait_time}
                      onChange={(e) => setForm(prev => ({ ...prev, max_wait_time: parseInt(e.target.value) }))}
                      style={styles.input}
                      min="30"
                      max="3600"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>מספר ממתינים מקסימלי</label>
                    <input
                      type="number"
                      value={form.max_callers}
                      onChange={(e) => setForm(prev => ({ ...prev, max_callers: parseInt(e.target.value) }))}
                      style={styles.input}
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>זמן wrap-up (שניות)</label>
                    <input
                      type="number"
                      value={form.wrap_up_time}
                      onChange={(e) => setForm(prev => ({ ...prev, wrap_up_time: parseInt(e.target.value) }))}
                      style={styles.input}
                      min="0"
                      max="120"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={form.position_announcement}
                        onChange={(e) => setForm(prev => ({ ...prev, position_announcement: e.target.checked }))}
                      />
                      <span>הודעת מיקום בתור</span>
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>הודעת פתיחה</label>
                  <input
                    type="text"
                    value={form.welcome_message}
                    onChange={(e) => setForm(prev => ({ ...prev, welcome_message: e.target.value }))}
                    style={styles.input}
                    placeholder="הודעה שתושמע בתחילת השיחה"
                  />
                </div>
              </div>

              {/* Profiles */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>פרופילים</h4>
                <p style={styles.sectionDesc}>בחר פרופילים שיכולים לענות לשיחות מתור זה</p>
                <div style={styles.profilesGrid}>
                  {PROFILES.map(profile => (
                    <button
                      key={profile.id}
                      onClick={() => toggleProfile(profile.id)}
                      style={{
                        ...styles.profileButton,
                        ...(form.profiles.includes(profile.id) ? styles.profileButtonActive : {})
                      }}
                    >
                      {profile.label}
                      {form.profiles.includes(profile.id) && ' ✓'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Members */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>נציגים</h4>
                <p style={styles.sectionDesc}>
                  {form.profiles.length > 0
                    ? 'בחר נציגים מהפרופילים שנבחרו'
                    : 'בחר נציגים לתור זה (או בחר פרופילים לסינון)'}
                </p>
                <div style={styles.membersGrid}>
                  {filteredUsers.length === 0 ? (
                    <div style={styles.noUsersMessage}>
                      {form.profiles.length > 0
                        ? 'אין משתמשים בפרופילים שנבחרו'
                        : 'אין משתמשים במערכת'}
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <label key={user.email} style={styles.memberItem}>
                        <input
                          type="checkbox"
                          checked={form.members.includes(user.email)}
                          onChange={() => toggleMember(user.email)}
                        />
                        <div style={styles.memberInfo}>
                          <span style={styles.memberName}>
                            {user.firstName || user.name || user.email}
                          </span>
                          {user.extension_number && (
                            <span style={styles.memberExt}>שלוחה {user.extension_number}</span>
                          )}
                        </div>
                        {user.profile && (
                          <span style={styles.memberProfile}>
                            {PROFILES.find(p => p.id === user.profile)?.label || user.profile}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Working Hours */}
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>שעות פעילות</h4>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.workingHours.enabled}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      workingHours: { ...prev.workingHours, enabled: e.target.checked }
                    }))}
                  />
                  <span>הגבל לשעות פעילות</span>
                </label>

                {form.workingHours.enabled && (
                  <div style={styles.workingHoursConfig}>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>שעת התחלה</label>
                        <input
                          type="time"
                          value={form.workingHours.start}
                          onChange={(e) => setForm(prev => ({
                            ...prev,
                            workingHours: { ...prev.workingHours, start: e.target.value }
                          }))}
                          style={styles.input}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>שעת סיום</label>
                        <input
                          type="time"
                          value={form.workingHours.end}
                          onChange={(e) => setForm(prev => ({
                            ...prev,
                            workingHours: { ...prev.workingHours, end: e.target.value }
                          }))}
                          style={styles.input}
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>ימי פעילות</label>
                      <div style={styles.daysGrid}>
                        {DAYS.map(day => (
                          <button
                            key={day.id}
                            onClick={() => toggleDay(day.id)}
                            style={{
                              ...styles.dayButton,
                              ...(form.workingHours.days.includes(day.id) ? styles.dayButtonActive : {})
                            }}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Status */}
              <div style={styles.section}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>תור פעיל</span>
                </label>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button onClick={() => setShowEditor(false)} style={styles.cancelBtn}>
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || saving}
                style={styles.saveBtn}
              >
                {saving ? 'שומר...' : 'שמור'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '24px',
    direction: 'rtl',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    color: '#181818'
  },
  subtitle: {
    margin: '8px 0 0 0',
    color: '#706e6b',
    fontSize: '14px'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#0176d3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#706e6b'
  },
  empty: {
    textAlign: 'center',
    padding: '60px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px'
  },
  emptyButton: {
    marginTop: '16px',
    padding: '10px 24px',
    backgroundColor: '#0176d3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  queuesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  queueCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  queueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  queueInfo: {},
  queueName: {
    fontSize: '16px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  queueIcon: {
    fontSize: '20px'
  },
  extensionBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    backgroundColor: '#e1f5fe',
    color: '#0176d3',
    borderRadius: '4px'
  },
  inactiveBadge: {
    fontSize: '11px',
    padding: '2px 8px',
    backgroundColor: '#f5f5f5',
    color: '#706e6b',
    borderRadius: '4px'
  },
  queueDescription: {
    marginTop: '4px',
    color: '#706e6b',
    fontSize: '13px'
  },
  queueActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  toggleInput: {
    display: 'none'
  },
  toggleSwitch: {
    width: '44px',
    height: '24px',
    borderRadius: '12px',
    position: 'relative',
    transition: 'background-color 0.2s'
  },
  toggleKnob: {
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    position: 'absolute',
    top: '2px',
    right: '2px',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
  },
  iconButton: {
    padding: '8px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px'
  },
  queueDetails: {
    marginTop: '16px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px'
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  detailLabel: {
    fontSize: '12px',
    color: '#706e6b'
  },
  detailValue: {
    fontSize: '13px',
    color: '#181818'
  },
  membersList: {
    marginTop: '12px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  memberChip: {
    padding: '4px 10px',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    borderRadius: '12px',
    fontSize: '12px'
  },
  memberMore: {
    padding: '4px 10px',
    backgroundColor: '#f5f5f5',
    color: '#706e6b',
    borderRadius: '12px',
    fontSize: '12px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '700px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e5e5e5'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#706e6b'
  },
  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #e5e5e5'
  },
  section: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f0f0f0'
  },
  sectionTitle: {
    margin: '0 0 8px 0',
    fontSize: '15px',
    fontWeight: '600',
    color: '#181818'
  },
  sectionDesc: {
    margin: '0 0 12px 0',
    fontSize: '13px',
    color: '#706e6b'
  },
  formRow: {
    display: 'flex',
    gap: '16px'
  },
  formGroup: {
    flex: 1,
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '13px',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d8dde6',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d8dde6',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  profilesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  profileButton: {
    padding: '8px 16px',
    border: '1px solid #d8dde6',
    borderRadius: '20px',
    backgroundColor: 'white',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  profileButtonActive: {
    backgroundColor: '#e3f2fd',
    borderColor: '#0176d3',
    color: '#0176d3'
  },
  membersGrid: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    padding: '8px'
  },
  noUsersMessage: {
    padding: '16px',
    textAlign: 'center',
    color: '#706e6b',
    fontSize: '14px'
  },
  memberItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '4px'
  },
  memberInfo: {
    flex: 1
  },
  memberName: {
    fontSize: '14px',
    display: 'block'
  },
  memberExt: {
    fontSize: '11px',
    color: '#0176d3'
  },
  memberProfile: {
    fontSize: '11px',
    color: '#706e6b',
    padding: '2px 6px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '12px'
  },
  workingHoursConfig: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  daysGrid: {
    display: 'flex',
    gap: '8px'
  },
  dayButton: {
    width: '40px',
    height: '40px',
    border: '1px solid #d8dde6',
    borderRadius: '8px',
    backgroundColor: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  dayButtonActive: {
    backgroundColor: '#0176d3',
    borderColor: '#0176d3',
    color: 'white'
  },
  cancelBtn: {
    padding: '10px 24px',
    backgroundColor: '#f5f5f5',
    color: '#181818',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  saveBtn: {
    padding: '10px 24px',
    backgroundColor: '#0176d3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default TelephonyQueueManager;
